import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Divider,
  Skeleton,
  Badge,
  Progress,
  CloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import Chart from 'react-apexcharts';

import Card from 'components/card/Card';
import { apiGetDrillDown } from 'api';
import {
  RAG_COLORS,
  formatCurrency,
  formatYearMonth,
  type RAGStatus,
} from 'utils/rag';
import { TrendsChart } from './TrendsChart';

// ── Types ────────────────────────────────────────────────────────────────────

interface Target {
  type: 'branch' | 'broker';
  id: string;
}

interface Props {
  target: Target | null;
  month: string;
  defaultTrends: any[];
  defaultTrendsScope?: string;
  isAdmin: boolean;
  onClear: () => void;
  onTargetChange?: (target: Target) => void;
}

// ── Score breakdown bars ──────────────────────────────────────────────────────

interface ScoreComponent {
  label: string;
  value: number | null;
  max: number;
  color: string;
}

function ScoreBreakdown({
  summary,
  isLoading,
}: {
  summary: any;
  isLoading: boolean;
}) {
  const total: number | null = summary?.broker_score ?? null;

  const components: ScoreComponent[] = [
    {
      label: 'Broker Productivity',
      value: summary?.broker_score_productivity ?? null,
      max: 40,
      color: 'purple',
    },
    {
      label: 'Performance to Plan',
      value: summary?.broker_score_p2p ?? null,
      max: 30,
      color: 'orange',
    },
    {
      label: 'Quotes Per Day',
      value: summary?.broker_score_quotes ?? null,
      max: 15,
      color: 'blue',
    },
    {
      label: 'Closing Ratio',
      value: summary?.broker_score_closing ?? null,
      max: 15,
      color: 'green',
    },
  ];

  if (isLoading) {
    return (
      <Box>
        <Skeleton h="14px" mb="12px" borderRadius="6px" />
        <Skeleton h="40px" mb="8px" borderRadius="6px" />
        <Skeleton h="40px" mb="8px" borderRadius="6px" />
        <Skeleton h="40px" mb="8px" borderRadius="6px" />
        <Skeleton h="40px" borderRadius="6px" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Total score — top line */}
      <Flex align="baseline" gap="6px" mb="16px">
        <Text fontSize="3xl" fontWeight="800" color="gray.800" lineHeight="1">
          {total == null ? '—' : Math.round(total)}
        </Text>
        <Text fontSize="sm" color="gray.400" fontWeight="400">
          / 100
        </Text>
        <Text
          fontSize="xs"
          fontWeight="700"
          color="gray.500"
          letterSpacing="wider"
          ml="4px"
        >
          BROKER SCORE
        </Text>
      </Flex>
      {components.map((c) => {
        const pct =
          c.value != null && c.max > 0
            ? Math.min(100, (c.value / c.max) * 100)
            : 0;
        return (
          <Box key={c.label} mb="10px">
            <Flex justify="space-between" mb="4px">
              <Text fontSize="xs" color="gray.600">
                {c.label}
              </Text>
              <Text fontSize="xs" fontWeight="600" color="gray.700">
                {c.value == null ? '—' : Math.round(c.value)}{' '}
                <Text as="span" color="gray.400">
                  / {c.max}
                </Text>
              </Text>
            </Flex>
            <Progress
              value={pct}
              size="md"
              colorScheme={c.color}
              borderRadius="6px"
              bg="gray.100"
            />
          </Box>
        );
      })}
    </Box>
  );
}

// ── P2P donut — replaces Lead Status donut ────────────────────────────────────

function P2PDonut({
  summary,
  isLoading,
}: {
  summary: any;
  isLoading: boolean;
}) {
  const p2pPct = Math.round(summary?.p2p_percent ?? 0); // real value, no cap
  const p2pGoal = summary?.p2p_goal ?? 0;
  const isOver = p2pPct >= 100;

  // Donut always shows a filled arc up to 100 (capped for visual), but displays real % in centre
  const filled = Math.min(p2pPct, 100);
  const remaining = Math.max(0, 100 - filled);
  const fillColor = isOver ? '#38A169' : p2pPct >= 80 ? '#ECC94B' : '#E53E3E';
  const bgColor = isOver ? '#C6F6D5' : p2pPct >= 80 ? '#FEF9C3' : '#FED7D7';

  const options = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      labels: ['P2P', 'Remaining'],
      colors: [fillColor, bgColor],
      legend: { show: false },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'P2P',
                fontSize: '11px',
                fontWeight: 600,
                color: '#718096',
                formatter: () => `${p2pPct}%`, // real value shown in centre
              },
              value: { fontSize: '16px', fontWeight: 700 },
            },
          },
        },
      },
      tooltip: { enabled: false },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [p2pPct, fillColor, bgColor],
  );

  if (isLoading) return <Skeleton h="200px" borderRadius="8px" />;

  return (
    <Box>
      <Text
        fontSize="xs"
        fontWeight="700"
        color="gray.500"
        letterSpacing="wider"
        mb="8px"
      >
        PERFORMANCE TO PLAN
      </Text>
      {p2pGoal === 0 ? (
        <Flex h="140px" align="center" justify="center">
          <Text fontSize="sm" color="gray.400">
            No P2P data
          </Text>
        </Flex>
      ) : (
        <>
          <Chart
            options={options}
            series={[filled, remaining]}
            type="donut"
            height={200}
          />
          <Text fontSize="xs" color="gray.400" textAlign="center" mt="-4px">
            Goal: {formatCurrency(p2pGoal)}
          </Text>
        </>
      )}
    </Box>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  won: { bg: '#C6F6D5', text: '#276749' },
  archived: { bg: '#FED7D7', text: '#9B2C2C' },
  lost: { bg: '#FED7D7', text: '#742A2A' },
  new: { bg: '#BEE3F8', text: '#2C5282' },
  'in progress': { bg: '#FEEBC8', text: '#7B341E' },
  in_progress: { bg: '#FEEBC8', text: '#7B341E' },
  'in-progress': { bg: '#FEEBC8', text: '#7B341E' },
};

function StatusBadge({ status }: { status: string }) {
  const key = (status || '').toLowerCase();
  const c = STATUS_COLORS[key] ?? { bg: '#EDF2F7', text: '#4A5568' };
  return (
    <Box
      display="inline-block"
      px="7px"
      py="1px"
      borderRadius="5px"
      bg={c.bg}
      color={c.text}
      fontWeight="600"
      fontSize="10px"
      whiteSpace="nowrap"
    >
      {status || '—'}
    </Box>
  );
}

// ── Lead list table (broker drill-down only) ──────────────────────────────────

function LeadTable({ leads, isLoading }: { leads: any[]; isLoading: boolean }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filtered = useMemo(() => {
    let rows = leads;
    if (statusFilter) {
      rows = rows.filter(
        (l) => (l.status || '').toLowerCase() === statusFilter.toLowerCase(),
      );
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (l) =>
          `${l.first_name} ${l.last_name}`.toLowerCase().includes(q) ||
          (l.lead_id || '').toLowerCase().includes(q) ||
          (l.province || '').toLowerCase().includes(q) ||
          (l.lead_source || '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [leads, search, statusFilter]);

  // Distinct statuses for filter dropdown
  const statuses = useMemo(() => {
    const s = new Set(leads.map((l) => l.status).filter(Boolean));
    return Array.from(s).sort();
  }, [leads]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton h="32px" mb="10px" borderRadius="6px" />
        <Skeleton h="220px" borderRadius="6px" />
      </Box>
    );
  }

  if (!leads || leads.length === 0) {
    return (
      <Box
        textAlign="center"
        py="24px"
        color="gray.400"
        fontSize="sm"
        bg="gray.50"
        borderRadius="8px"
        border="1px dashed"
        borderColor="gray.200"
      >
        No leads found for this broker in the selected month.
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Flex gap="10px" mb="10px" wrap="wrap">
        <Input
          placeholder="Search name, lead ID, province…"
          size="sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="240px"
          borderRadius="6px"
          fontSize="xs"
        />
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          maxW="160px"
          borderRadius="6px"
          fontSize="xs"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Text fontSize="xs" color="gray.400" alignSelf="center">
          {filtered.length} of {leads.length} leads
        </Text>
      </Flex>

      <Box
        overflowX="auto"
        borderRadius="8px"
        border="1px solid"
        borderColor="gray.200"
        maxH="400px"
        overflowY="auto"
      >
        <Table size="sm" variant="simple">
          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th minW="130px">Name</Th>
              <Th>Province</Th>
              <Th>Status</Th>
              <Th>Source</Th>
              <Th>Workflow</Th>
              <Th textAlign="right">Value</Th>
              <Th>Created</Th>
              <Th textAlign="right">Response</Th>
              <Th>Lost Reason</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((lead, idx) => {
              const name =
                [lead.first_name, lead.last_name].filter(Boolean).join(' ') ||
                '—';
              const created = lead.lead_created
                ? new Date(lead.lead_created).toLocaleDateString('en-CA', {
                    month: 'short',
                    day: 'numeric',
                  })
                : '—';
              const responseLabel =
                lead.response_time_min != null
                  ? lead.response_time_min < 60
                    ? `${lead.response_time_min}m`
                    : `${Math.round(lead.response_time_min / 60)}h`
                  : '—';

              return (
                <Tr key={lead.lead_id || idx} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <Text fontSize="xs" fontWeight="600" color="gray.700">
                      {name}
                    </Text>
                    {lead.phone_number && (
                      <Text fontSize="10px" color="gray.400">
                        {lead.phone_number}
                      </Text>
                    )}
                    {lead.px_lead && (
                      <Badge
                        colorScheme="purple"
                        fontSize="9px"
                        ml="0"
                        mt="2px"
                      >
                        PX
                      </Badge>
                    )}
                  </Td>
                  <Td fontSize="xs" color="gray.600">
                    {lead.province || '—'}
                  </Td>
                  <Td>
                    <StatusBadge status={lead.status} />
                  </Td>
                  <Td fontSize="xs" color="gray.600">
                    {lead.lead_source || '—'}
                  </Td>
                  <Td fontSize="xs" color="gray.500">
                    {lead.lead_workflow || '—'}
                  </Td>
                  <Td textAlign="right" fontSize="xs" color="gray.700">
                    {lead.value != null
                      ? `$${Math.round(lead.value).toLocaleString()}`
                      : '—'}
                  </Td>
                  <Td fontSize="xs" color="gray.500">
                    {created}
                  </Td>
                  <Td
                    textAlign="right"
                    fontSize="xs"
                    color={
                      lead.response_time_min != null &&
                      lead.response_time_min > 60
                        ? 'orange.500'
                        : 'gray.600'
                    }
                  >
                    {responseLabel}
                  </Td>
                  <Td fontSize="xs" color="gray.500" maxW="120px">
                    <Text noOfLines={1} title={lead.lost_reason || undefined}>
                      {lead.lost_reason || '—'}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

// ── Entity detail panel (branch or broker) ────────────────────────────────────

type BrokerSortKey =
  | 'score'
  | 'premium_score'
  | 'quote_volume_score'
  | 'closing_ratio_score'
  | 'performance_score'
  | 'won_quotes'
  | 'archived'
  | 'aht_new'
  | 'aht_in_progress'
  | 'aht_lost'
  | 'aht_source_organic'
  | 'aht_source_agg'
  | 'aht_source_cl'
  | 'aht_source_mychoice';

function EntityPanel({
  data,
  target,
  isLoading,
  onBrokerClick,
  month,
  entityLabel,
}: {
  data: any;
  target: Target;
  isLoading: boolean;
  onBrokerClick?: (email: string) => void;
  month: string;
  entityLabel: string;
}) {
  const s = data?.summary ?? {};

  const [brokerSortKey, setBrokerSortKey] = useState<BrokerSortKey>('score');
  const [brokerSortDir, setBrokerSortDir] = useState<'asc' | 'desc'>('desc');

  const handleBrokerSort = (col: BrokerSortKey) => {
    if (col === brokerSortKey) {
      setBrokerSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setBrokerSortKey(col);
      setBrokerSortDir('desc');
    }
  };

  const sortedBrokers = useMemo(() => {
    const brokers: any[] = data?.brokers ?? [];
    return [...brokers].sort((a, b) => {
      const diff = (a[brokerSortKey] ?? 0) - (b[brokerSortKey] ?? 0);
      return brokerSortDir === 'desc' ? -diff : diff;
    });
  }, [data?.brokers, brokerSortKey, brokerSortDir]);

  return (
    <Box>
      <Divider mb="16px" />

      {/* Score breakdown (left) + P2P donut (right) */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="24px" mb="4px">
        <ScoreBreakdown summary={s} isLoading={isLoading} />
        <P2PDonut summary={s} isLoading={isLoading} />
      </SimpleGrid>

      {/* Lead list — broker drill-down only */}
      {target.type === 'broker' && (
        <>
          <Divider my="16px" />
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            letterSpacing="wider"
            mb="10px"
          >
            LEADS FOR {formatYearMonth(month).toUpperCase()} — BROKER:{' '}
            {entityLabel.toUpperCase()}
          </Text>
          <LeadTable leads={data?.leads ?? []} isLoading={isLoading} />
        </>
      )}

      {/* Broker list — branch only */}
      {!isLoading && target.type === 'branch' && sortedBrokers.length > 0 && (
        <>
          <Divider my="16px" />
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            letterSpacing="wider"
            mb="10px"
          >
            BROKERS FOR {formatYearMonth(month).toUpperCase()} — BRANCH:{' '}
            {entityLabel.toUpperCase()}
          </Text>
          <Box
            overflowX="auto"
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.200"
          >
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th minW="140px">Broker</Th>
                  {(
                    [
                      {
                        label: 'Score',
                        key: 'score' as BrokerSortKey,
                        color: undefined,
                      },
                      {
                        label: 'Prem.',
                        key: 'premium_score' as BrokerSortKey,
                        color: undefined,
                      },
                      {
                        label: 'Vol.',
                        key: 'quote_volume_score' as BrokerSortKey,
                        color: undefined,
                      },
                      {
                        label: 'Closing',
                        key: 'closing_ratio_score' as BrokerSortKey,
                        color: undefined,
                      },
                      {
                        label: 'Perf.',
                        key: 'performance_score' as BrokerSortKey,
                        color: undefined,
                      },
                      {
                        label: 'Won',
                        key: 'won_quotes' as BrokerSortKey,
                        color: 'green.600',
                      },
                      {
                        label: 'Archived',
                        key: 'archived' as BrokerSortKey,
                        color: 'red.500',
                      },
                    ] as { label: string; key: BrokerSortKey; color?: string }[]
                  ).map(({ label, key, color }) => (
                    <Th
                      key={key}
                      textAlign="center"
                      cursor="pointer"
                      userSelect="none"
                      color={brokerSortKey === key ? 'brand.600' : color}
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleBrokerSort(key)}
                    >
                      <Flex align="center" justify="center" gap="3px">
                        {label}
                        {brokerSortKey === key && (
                          <Text as="span" fontSize="9px">
                            {brokerSortDir === 'desc' ? '▼' : '▲'}
                          </Text>
                        )}
                      </Flex>
                    </Th>
                  ))}
                  {/* Lead status from vw_Lead_AHT */}
                  {(
                    [
                      {
                        label: 'New',
                        key: 'aht_new' as BrokerSortKey,
                        color: 'blue.500',
                        borderLeft: true,
                      },
                      {
                        label: 'In-Prog',
                        key: 'aht_in_progress' as BrokerSortKey,
                        color: 'orange.500',
                        borderLeft: false,
                      },
                      {
                        label: 'Lost',
                        key: 'aht_lost' as BrokerSortKey,
                        color: 'red.400',
                        borderLeft: false,
                      },
                    ] as {
                      label: string;
                      key: BrokerSortKey;
                      color: string;
                      borderLeft: boolean;
                    }[]
                  ).map(({ label, key, color, borderLeft }) => (
                    <Th
                      key={key}
                      textAlign="center"
                      cursor="pointer"
                      userSelect="none"
                      borderLeftWidth={borderLeft ? '1px' : undefined}
                      borderLeftColor={borderLeft ? 'gray.200' : undefined}
                      color={brokerSortKey === key ? 'brand.600' : color}
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleBrokerSort(key)}
                    >
                      <Flex align="center" justify="center" gap="3px">
                        {label}
                        {brokerSortKey === key && (
                          <Text as="span" fontSize="9px">
                            {brokerSortDir === 'desc' ? '▼' : '▲'}
                          </Text>
                        )}
                      </Flex>
                    </Th>
                  ))}
                  {/* Lead source from vw_Lead_AHT */}
                  {(
                    [
                      {
                        label: 'Organic',
                        key: 'aht_source_organic' as BrokerSortKey,
                        borderLeft: true,
                      },
                      {
                        label: 'Agg.',
                        key: 'aht_source_agg' as BrokerSortKey,
                        borderLeft: false,
                      },
                      {
                        label: 'CL',
                        key: 'aht_source_cl' as BrokerSortKey,
                        borderLeft: false,
                      },
                      {
                        label: 'MC',
                        key: 'aht_source_mychoice' as BrokerSortKey,
                        borderLeft: false,
                      },
                    ] as {
                      label: string;
                      key: BrokerSortKey;
                      borderLeft: boolean;
                    }[]
                  ).map(({ label, key, borderLeft }) => (
                    <Th
                      key={key}
                      textAlign="center"
                      cursor="pointer"
                      userSelect="none"
                      borderLeftWidth={borderLeft ? '1px' : undefined}
                      borderLeftColor={borderLeft ? 'gray.200' : undefined}
                      color={brokerSortKey === key ? 'brand.600' : 'gray.500'}
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleBrokerSort(key)}
                    >
                      <Flex align="center" justify="center" gap="3px">
                        {label}
                        {brokerSortKey === key && (
                          <Text as="span" fontSize="9px">
                            {brokerSortDir === 'desc' ? '▼' : '▲'}
                          </Text>
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {sortedBrokers.map((b: any) => {
                  const sc = RAG_COLORS[b.rag_score as RAGStatus];
                  const bWon = b.won_quotes ?? 0;
                  const bArchived = b.archived ?? 0;
                  const clickable = !!onBrokerClick;
                  const hasAHT = (b.aht_total ?? 0) > 0;
                  return (
                    <Tr
                      key={b.email}
                      _hover={{ bg: clickable ? 'brand.50' : 'gray.50' }}
                      cursor={clickable ? 'pointer' : undefined}
                      onClick={
                        clickable ? () => onBrokerClick(b.email) : undefined
                      }
                    >
                      <Td>
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color={clickable ? 'brand.600' : 'gray.700'}
                          _hover={
                            clickable
                              ? { textDecoration: 'underline' }
                              : undefined
                          }
                        >
                          {b.label}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {b.email}
                        </Text>
                        {b.broker_csr_code && (
                          <Text fontSize="10px" color="gray.300">
                            CSR: {b.broker_csr_code}
                          </Text>
                        )}
                      </Td>
                      <Td textAlign="center">
                        <Box
                          display="inline-block"
                          minW="36px"
                          px="6px"
                          py="1px"
                          borderRadius="5px"
                          bg={sc?.bg}
                          color={sc?.text}
                          fontWeight="600"
                          fontSize="xs"
                          textAlign="center"
                        >
                          {Math.round(b.score)}
                        </Box>
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {Math.round(b.premium_score ?? 0)}
                        <Text as="span" color="gray.400">
                          /40
                        </Text>
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {Math.round(b.quote_volume_score ?? 0)}
                        <Text as="span" color="gray.400">
                          /20
                        </Text>
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {Math.round(b.closing_ratio_score ?? 0)}
                        <Text as="span" color="gray.400">
                          /20
                        </Text>
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {Math.round(b.performance_score ?? 0)}
                        <Text as="span" color="gray.400">
                          /20
                        </Text>
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize="xs"
                        color="green.600"
                        fontWeight="500"
                      >
                        {bWon}
                      </Td>
                      <Td textAlign="center" fontSize="xs" color="red.500">
                        {bArchived}
                      </Td>
                      {/* AHT status */}
                      <Td
                        textAlign="center"
                        fontSize="xs"
                        borderLeftWidth="1px"
                        borderLeftColor="gray.100"
                        color={hasAHT ? 'blue.500' : 'gray.300'}
                        fontWeight={hasAHT ? '600' : '400'}
                      >
                        {b.aht_new ?? 0}
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize="xs"
                        color={hasAHT ? 'orange.500' : 'gray.300'}
                        fontWeight={hasAHT ? '600' : '400'}
                      >
                        {b.aht_in_progress ?? 0}
                      </Td>
                      <Td
                        textAlign="center"
                        fontSize="xs"
                        color={hasAHT ? 'red.400' : 'gray.300'}
                        fontWeight={hasAHT ? '500' : '400'}
                      >
                        {b.aht_lost ?? 0}
                      </Td>
                      {/* AHT source */}
                      <Td
                        textAlign="center"
                        fontSize="xs"
                        borderLeftWidth="1px"
                        borderLeftColor="gray.100"
                        color="gray.600"
                      >
                        {b.aht_source_organic ?? 0}
                      </Td>
                      <Td textAlign="center" fontSize="xs" color="gray.600">
                        {b.aht_source_agg ?? 0}
                      </Td>
                      <Td textAlign="center" fontSize="xs" color="gray.600">
                        {b.aht_source_cl ?? 0}
                      </Td>
                      <Td textAlign="center" fontSize="xs" color="gray.600">
                        {b.aht_source_mychoice ?? 0}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Main DetailPanel ──────────────────────────────────────────────────────────
// Layout:
//   Card 1 — always visible: rolling 12-month overview trend (fixed size)
//   Card 2 — only when target selected: entity drill-down detail appears below,
//             scrolled into view automatically on each new selection

export const DetailPanel: React.FC<Props> = ({
  target,
  month,
  defaultTrends,
  defaultTrendsScope = 'BIG',
  isAdmin,
  onClear,
  onTargetChange,
}) => {
  const trendCardRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['drill-down', target?.type, target?.id, month],
    queryFn: () =>
      apiGetDrillDown({
        type: target!.type,
        id: target!.id,
        month,
      }) as Promise<any>,
    enabled: !!target,
    staleTime: 10 * 60 * 1000,
  });

  // Scroll to the R12 trend card (Card 1) whenever a new target is selected so the
  // updated chart is immediately visible before the detail panel below it.
  useEffect(() => {
    if (target && trendCardRef.current) {
      trendCardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [target?.type, target?.id]);

  const label = isLoading ? '...' : (data?.label ?? '');
  const entityType = target?.type === 'branch' ? 'Branch' : 'Broker';

  const handleBrokerClick = onTargetChange
    ? (email: string) => onTargetChange({ type: 'broker', id: email })
    : undefined;

  const branchId = data?.summary?.branch_id;
  const branchName = data?.summary?.branch_name;
  const handleBranchClick =
    onTargetChange && branchId
      ? () => onTargetChange({ type: 'branch', id: String(branchId) })
      : undefined;

  // Chart shows entity trends when a target is loaded, otherwise falls back to aggregate.
  // Only fall back to BIG/default trends when no target is selected — while a target is
  // loading or has loaded with empty trends, keep the previous trends visible to avoid
  // a flash of BIG data under the entity label.
  const hasEntityTrends =
    target && !isLoading && (data?.trends ?? []).length > 0;
  const trendData = hasEntityTrends
    ? data.trends
    : !target
      ? defaultTrends // no selection → show aggregate (BIG / branch)
      : defaultTrends; // selected but loading / no R12 yet → show aggregate while waiting
  const trendsScope = hasEntityTrends
    ? `${label} ${entityType}`
    : defaultTrendsScope;

  // chartKey forces a full TrendsChart remount when the scope changes so ApexCharts
  // does not mutate the existing chart in place (stale axis ranges, series state).
  const chartKey = trendsScope;

  return (
    <>
      {/* Card 1 — always visible rolling 12-month trend; switches to entity data when selected */}
      <Card
        mb="24px"
        p="20px"
        ref={trendCardRef}
        sx={{ scrollMarginTop: '80px' }}
      >
        {isLoading && !!target ? (
          <Skeleton h="200px" borderRadius="8px" />
        ) : (
          <TrendsChart
            key={chartKey}
            data={trendData}
            isAdmin={isAdmin}
            trendsScope={trendsScope}
          />
        )}
      </Card>

      {/* Card 2 — entity detail: score breakdown + donut + leads/brokers */}
      {target && (
        <Card mb="24px" p="20px" ref={detailRef}>
          {/* Header */}
          <Flex align="center" justify="space-between" mb="16px">
            <Flex align="center" gap="10px" flexWrap="wrap">
              {/* Branch breadcrumb — visible when viewing a broker, click to go up to branch */}
              {target.type === 'broker' && handleBranchClick && (
                <Badge
                  colorScheme="brand"
                  fontSize="xs"
                  cursor="pointer"
                  onClick={handleBranchClick}
                  _hover={{ opacity: 0.75 }}
                  title={`View ${branchName ?? 'branch'} branch`}
                >
                  ↑ {branchName ?? `Branch ${branchId}`}
                </Badge>
              )}
              <Text fontSize="sm" fontWeight="700" color="gray.700">
                {label}
              </Text>
              <Badge
                colorScheme={target.type === 'branch' ? 'brand' : 'green'}
                fontSize="xs"
              >
                {entityType}
              </Badge>
            </Flex>
            <Flex align="center" gap="8px">
              <Text fontSize="xs" color="gray.400">
                {month}
              </Text>
              <CloseButton size="sm" onClick={onClear} />
            </Flex>
          </Flex>

          <EntityPanel
            data={data}
            target={target}
            isLoading={isLoading}
            onBrokerClick={handleBrokerClick}
            month={month}
            entityLabel={label}
          />
        </Card>
      )}
    </>
  );
};
